import { api } from '@/lib/axios';

export interface Position {
  id: string;
  name: string;
  description?: string;
  department?: string;
  level?: string;
  baseSalary?: number;
  benefits?: string;
  requirements?: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePositionDto {
  name: string;
  description?: string;
  department?: string;
  level?: string;
  baseSalary?: number;
  benefits?: string;
  requirements?: string;
  active?: boolean;
}

export interface UpdatePositionDto extends Partial<CreatePositionDto> {}

export interface PositionsResponse {
  data: Position[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface PositionProcess {
  associationId: string;
  associatedAt: string;
  process: {
    id: string;
    name: string;
    description?: string;
    type?: string;
  };
}

export const positionsService = {
  async getAll(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
    department?: string;
    active?: boolean;
    type?: string;
  }): Promise<PositionsResponse> {
    const { data } = await api.get('/positions', { params });
    return data;
  },

  async getById(id: string): Promise<Position> {
    const { data } = await api.get(`/positions/${id}`);
    return data;
  },

  async create(position: CreatePositionDto): Promise<Position> {
    const { data } = await api.post('/positions', position);
    return data;
  },

  async update(id: string, position: UpdatePositionDto): Promise<Position> {
    const { data } = await api.patch(`/positions/${id}`, position);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/positions/${id}`);
  },

  async toggleActive(id: string): Promise<Position> {
    const { data } = await api.patch(`/positions/${id}/toggle-active`);
    return data;
  },

  async findByDepartment(department: string): Promise<Position[]> {
    const { data } = await api.get(`/positions/department/${department}`);
    return data;
  },

  async getStatistics(): Promise<any> {
    const { data } = await api.get('/positions/statistics');
    return data;
  },

  // Process association methods
  async getProcesses(positionId: string): Promise<PositionProcess[]> {
    const { data } = await api.get(`/positions/${positionId}/processes`);
    return data;
  },

  async associateProcess(positionId: string, processId: string): Promise<PositionProcess> {
    const { data } = await api.post(`/positions/${positionId}/processes`, { processId });
    return data;
  },

  async associateMultipleProcesses(positionId: string, processIds: string[]): Promise<any> {
    const { data } = await api.post(`/positions/${positionId}/processes/bulk`, { processIds });
    return data;
  },

  async removeProcessAssociation(positionId: string, processId: string): Promise<void> {
    await api.delete(`/positions/${positionId}/processes/${processId}`);
  },
};
